import {
  HelpCircle,
  Building2,
  FileCheck,
  ListChecks,
  Users,
  Search,
  Mail,
  Phone,
  MapPin,
  MessageCircle,
} from "lucide-react";

/**
 * Tutorial de Registro Legal para usuarios externos (Tramitador, Titular, Gestor, Representante).
 * Versión React/JSX del HTML original /opt/SICEN/Insumos/Tutoriales/Tutorial Registro Legal Externos.html.
 */
export function TutorialExternos() {
  return (
    <div className="space-y-5">
      <header className="rounded-lg border bg-surface p-6 shadow-sm">
        <div className="text-xs font-bold uppercase tracking-wider text-primary-strong">SICEN · Guía para usuarios</div>
        <h1 className="mt-2 text-2xl font-semibold text-fg">Registro Legal — usuarios externos</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-fg-muted">
          Esta guía te ayudará a entender qué es el Registro Legal, qué información necesitas preparar, cómo se
          realiza el alta dentro de SICEN y qué puedes consultar posteriormente en el sistema.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <aside className="self-start rounded-lg border bg-surface p-4 shadow-sm lg:sticky lg:top-4">
          <h3 className="mb-3 text-sm font-semibold text-fg">Contenido</h3>
          <ul className="space-y-1 text-sm">
            <li><a href="#que-es" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">1. ¿Qué es el Registro Legal?</a></li>
            <li><a href="#para-que" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">2. ¿Para qué sirve?</a></li>
            <li><a href="#quien" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">3. ¿Quién realiza el alta?</a></li>
            <li><a href="#preparar" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">4. ¿Qué debes preparar?</a></li>
            <li><a href="#proceso" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">5. ¿Cómo es el proceso?</a></li>
            <li><a href="#tipos" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">6. Tipos de registro</a></li>
            <li><a href="#despues" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">7. ¿Qué pasa después?</a></li>
            <li><a href="#consulta" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">8. Consulta de tu registro</a></li>
            <li><a href="#ayuda" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">9. Soporte y ayuda</a></li>
          </ul>
        </aside>

        <div className="space-y-6">
          <Section id="que-es" icon={<Building2 className="h-5 w-5" />} title="¿Qué es el Registro Legal?">
            <p>
              El Registro Legal es el expediente base con el que se da de alta a una empresa, persona física o
              tramitador dentro del sistema SICEN. Este registro reúne la información general, legal y de contacto
              necesaria para operar posteriormente solicitudes y trámites.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <FactCard label="Incluye" value="Datos generales, fiscales y de contacto" />
              <FactCard label="Puede integrar" value="Usuarios autorizados y representantes legales" />
              <FactCard label="Permite" value="Operar trámites posteriores dentro de SICEN" />
            </div>
          </Section>

          <Section id="para-que" icon={<FileCheck className="h-5 w-5" />} title="¿Para qué sirve?">
            <p>
              Sirve para formalizar tu alta dentro del sistema y dejar lista la base legal y operativa de tu empresa
              o persona. Sin este registro, no se puede avanzar de forma correcta en los procesos posteriores.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Identificación legal", "Alta de usuarios", "Control de expediente", "Trazabilidad", "Acceso a trámites"].map((tag) => (
                <span key={tag} className="rounded-full border bg-estado-info-bg px-3 py-1 text-xs font-semibold text-primary-strong">
                  {tag}
                </span>
              ))}
            </div>
          </Section>

          <Section id="quien" icon={<Users className="h-5 w-5" />} title="¿Quién realiza el alta?">
            <p>
              El alta del Registro Legal la realiza internamente el área de Calidad. Como usuario externo, tu
              participación principal consiste en compartir la información correcta, entregar la documentación
              requerida y validar que los datos capturados sean correctos.
            </p>
            <div className="rounded-lg border-l-4 border-primary-strong bg-estado-info-bg p-4 text-sm leading-relaxed text-fg-muted">
              Aunque el alta la realiza el área interna de Calidad, tu información y documentación son fundamentales
              para que el registro quede correcto desde el inicio.
            </div>
          </Section>

          <Section id="preparar" icon={<ListChecks className="h-5 w-5" />} title="¿Qué debes preparar?">
            <p>
              Antes de iniciar, conviene reunir la información básica de tu empresa o persona, así como los datos de
              los usuarios que quedarán asociados al expediente.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <FactCard label="Datos generales" value="Nombre o razón social, RFC, domicilio y datos de contacto" />
              <FactCard label="Usuarios" value="Correo electrónico del solicitante y de autorizados" />
              <FactCard label="Representación legal" value="Cuando aplique, datos de representantes legales" />
              <FactCard label="Documentación" value="Archivos o documentos de soporte conforme a tu tipo de registro" />
            </div>
            <div className="rounded-lg border-l-4 border-edge-strong bg-surface-2 p-4 text-sm leading-relaxed text-fg-muted">
              Procura enviar la información completa y revisada. Los errores en correo, RFC o tipo de registro suelen
              provocar retrabajo o ajustes posteriores.
            </div>
          </Section>

          <Section id="proceso" icon={<ChevronRightIcon />} title="¿Cómo es el proceso?">
            <p>De manera simple, el proceso ocurre así:</p>
            <ol className="space-y-3">
              <Step n={1} title="Compartes tu información y documentación">
                Entregas al área de atención o Calidad los datos y documentos necesarios para el alta.
              </Step>
              <Step n={2} title="El área de Calidad captura tu expediente en SICEN">
                Se registra tu empresa, persona o tramitador según el caso correspondiente.
              </Step>
              <Step n={3} title="Se asocian usuarios y contactos">
                Se registra el usuario principal y, si aplica, usuarios autorizados o relacionados.
              </Step>
              <Step n={4} title="Tu registro queda integrado en el sistema">
                Una vez concluido el alta, tu expediente base queda listo para los procesos posteriores.
              </Step>
            </ol>
          </Section>

          <Section id="tipos" icon={<Building2 className="h-5 w-5" />} title="Tipos de registro">
            <p>Dependiendo de tu caso, el Registro Legal puede corresponder a alguno de los siguientes escenarios:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li><strong>Empresa titular persona moral:</strong> cuando una empresa opera directamente sus trámites.</li>
              <li><strong>Empresa titular persona física:</strong> cuando el titular del expediente es una persona física.</li>
              <li><strong>Tramitador externo persona moral:</strong> cuando una empresa gestionará trámites para terceros.</li>
              <li><strong>Tramitador externo persona física:</strong> cuando una persona física gestionará trámites para terceros.</li>
              <li><strong>Empresa titular con tramitador:</strong> cuando una empresa trabaja apoyándose en un tramitador previamente registrado.</li>
            </ul>
            <div className="rounded-lg border-l-4 border-primary-strong bg-estado-info-bg p-4 text-sm leading-relaxed text-fg-muted">
              Es importante que desde el inicio se identifique el tipo correcto de registro, ya que esto puede cambiar
              la información y documentación requerida.
            </div>
          </Section>

          <Section id="despues" icon={<FileCheck className="h-5 w-5" />} title="¿Qué pasa después del alta?">
            <p>
              Después de que tu expediente queda registrado, el sistema puede utilizar esa base para los procesos
              posteriores. También puede quedar ligado a tus usuarios de acceso y a futuras operaciones dentro de
              SICEN.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <FactCard label="Tu expediente" value="Queda identificado en SICEN" />
              <FactCard label="Tus usuarios" value="Quedan asociados según el escenario registrado" />
              <FactCard label="Procesos posteriores" value="Podrán apoyarse en este registro base" />
            </div>
          </Section>

          <Section id="consulta" icon={<Search className="h-5 w-5" />} title="Consulta de tu registro">
            <p>
              Una vez que tu registro ya existe en SICEN, podrás consultar la información que te corresponda según tu
              rol y tus asociaciones dentro del sistema.
            </p>
            <ol className="space-y-3">
              <Step n={1} title="Ingresar con tu correo y contraseña">
                El acceso se realiza con las credenciales asociadas a tu usuario.
              </Step>
              <Step n={2} title="Entrar a la opción de consulta">
                La visibilidad dependerá del tipo de usuario y de las asociaciones existentes.
              </Step>
              <Step n={3} title="Revisar la información disponible">
                Podrás ver el registro o los registros que te correspondan según tu perfil.
              </Step>
            </ol>
          </Section>

          <Section id="ayuda" icon={<HelpCircle className="h-5 w-5" />} title="Soporte y ayuda">
            <p>
              Si tienes dudas sobre tu usuario, el tipo de registro que te corresponde o la documentación que debes
              enviar, puedes comunicarte con el contacto principal de apoyo para canalizar tu solicitud al área
              correspondiente.
            </p>
            <div className="rounded-lg border bg-surface-2/60 p-4">
              <h4 className="text-base font-semibold text-fg">América Muñiz Lara</h4>
              <p className="mt-1 text-sm leading-relaxed text-fg-muted">
                Contacto principal de apoyo para usuarios del sistema SICEN. Desde este punto se pueden canalizar
                dudas relacionadas con registro, acceso, seguimiento y atención a las diferentes secciones del
                sistema.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <ContactCard
                  icon={<Mail className="h-4 w-4" />}
                  label="Correo electrónico"
                  value="amuniz@npmx.org"
                  href="mailto:amuniz@npmx.org"
                />
                <ContactCard
                  icon={<MessageCircle className="h-4 w-4" />}
                  label="WhatsApp"
                  value="+52 1 56 1728 8722"
                  href="https://wa.me/5215617288722"
                />
                <ContactCard
                  icon={<Mail className="h-4 w-4" />}
                  label="Correo institucional"
                  value="contacto@npmx.org"
                  href="mailto:contacto@npmx.org"
                />
                <ContactCard
                  icon={<Phone className="h-4 w-4" />}
                  label="Teléfono"
                  value="(55) 1520-5386"
                  href="tel:+525515205386"
                />
                <div className="rounded border bg-surface p-3 sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                    <MapPin className="h-4 w-4" /> Dirección
                  </div>
                  <div className="mt-1 text-sm text-fg">
                    Alborada 124, Número interior 903, Col. Parque del Pedregal, Alcaldía Tlalpan, Ciudad de México,
                    C.P. 14010
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border-l-4 border-edge-strong bg-surface-2 p-4 text-sm leading-relaxed text-fg-muted">
              Antes de enviar tu solicitud de apoyo, procura tener a la mano tu nombre o razón social, RFC, correo
              registrado y una breve descripción de la duda o incidencia para agilizar la atención.
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  icon,
  children,
}: {
  id: string;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="space-y-3 rounded-lg border bg-surface p-6 shadow-sm">
      <h2 className="flex items-center gap-2 text-base font-semibold uppercase tracking-wide text-fg">
        <span className="text-primary-strong">{icon}</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-fg-muted">{children}</div>
    </section>
  );
}

function FactCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-surface-2/60 p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="mt-1 text-sm font-medium text-fg">{value}</div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
        {n}
      </span>
      <div>
        <div className="font-semibold text-fg">{title}</div>
        <p className="mt-0.5 text-sm text-fg-muted">{children}</p>
      </div>
    </li>
  );
}

function ContactCard({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href: string }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="block rounded border bg-surface p-3 hover:border-primary-strong"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        {icon} {label}
      </div>
      <div className="mt-1 text-sm font-medium text-primary-strong">{value}</div>
    </a>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
